import { FC, useCallback, useMemo, useState } from 'react';
import { Stack } from '@mui/material';
import debounce from 'lodash/debounce';
import ToolServiceButton from '@/components/tool-service-button';
import TextInput from '@/components/form/text-input';
import SelectMulti from '@/components/form/select-multi';
import { UiOption } from '@/lib/helpers';
import { McpBlockActionOptions } from '@/lib/models/policy-model-v1/mcp-controls';
import InlineToast from '@/components/toasts/inline';
import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import { useQuery } from 'react-query';
import { QueryKey } from '@/lib/query-client';
import { getAllMcpServers } from '@/lib/services/mcp.service';
import { McpServerModel } from '@/lib/models/mcp-server.model';

interface Props {
  data: UserEventModel;
}

// Build filter options including block actions, allow, executed, and classifications
const FILTER_OPTIONS: UiOption[] = [
  { label: 'Allow', value: 'allow' },
  { label: 'Executed', value: 'executed' },
  { label: 'Read', value: 'read' },
  { label: 'Write', value: 'write' },
  ...McpBlockActionOptions.filter((opt) => !opt.meta?.header),
];

const Tools: FC<Props> = ({ data }) => {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<UiOption[]>([]);
  const tools = useMemo(() => data?.tools || [], [data?.tools]);
  const hasTools = !!tools.length;

  const serverQuery = useQuery([QueryKey.McpServersList], async () => getAllMcpServers(), {
    enabled: hasTools,
  });
  const { data: mcpServers } = serverQuery;

  const serverMap: Map<string, McpServerModel> = useMemo(() => {
    const map: Map<string, McpServerModel> = new Map();
    mcpServers?.forEach((server) => {
      map.set(server.id, server);
    });
    return map;
  }, [mcpServers]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchChange = useCallback(
    debounce((value: string) => {
      handleSearchChange(value);
    }, 600),
    [handleSearchChange]
  );

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
    debouncedSearchChange(value);
  };

  const handleFiltersChange = (options: UiOption[]) => {
    setFilters(options);
  };

  const filteredTools = useMemo(() => {
    return tools
      .filter(
        ({ name, description, serverName, blocked, blockAction, execution, classification }) => {
          // Search filter - check name, description, and server name
          if (search) {
            const searchLower = search.toLowerCase();
            const searchTerms = [name, description, serverName].map((val) => val.toLowerCase());
            const matchesSearch = searchTerms.some((term) => term.includes(searchLower));
            if (!matchesSearch) {
              return false;
            }
          }

          // If no filters selected, show all tools
          if (filters.length === 0) {
            return true;
          }

          const filterValues = filters.map(({ value }) => value);

          // Check if tool matches any selected filter
          const isAllowed = !blocked || blockAction === 'ignore';
          const matchesAllow = filterValues.includes('allow') && isAllowed;
          const matchesExecuted = filterValues.includes('executed') && execution.detected;
          const matchesBlockAction =
            blocked && blockAction !== 'ignore' && filterValues.includes(blockAction);
          const matchesClassification = filterValues.includes(classification.action);

          return matchesAllow || matchesExecuted || matchesBlockAction || matchesClassification;
        }
      )
      .sort((a, b) => {
        // Sort executed tools first, then alphabetically within each group
        if (a.execution.detected && !b.execution.detected) {
          return -1;
        }
        if (!a.execution.detected && b.execution.detected) {
          return 1;
        }
        // Both have same execution status, sort alphabetically
        return a.name.localeCompare(b.name);
      });
  }, [tools, search, filters]);

  const emptyMessage = hasTools ? 'No tools match your search criteria' : 'No tools were found';

  const hasFilteredTools = !!filteredTools.length;

  return (
    <Stack gap={1} mt={2}>
      <Stack width="100%" gap={1} flexWrap="wrap">
        <TextInput
          placeholder="Search Tools"
          name="keyword"
          value={searchInput}
          onChange={handleSearchInput}
          size="small"
          clearable
          startIcon="search"
          disabled={!hasTools}
        />
        <SelectMulti
          name="filters"
          options={FILTER_OPTIONS}
          value={filters.map(({ value }) => value)}
          onChange={handleFiltersChange}
          size="small"
          label="Filters"
          disabled={!hasTools}
        />
      </Stack>
      <Stack gap={1}>
        {!hasFilteredTools && <InlineToast message={emptyMessage} level="info" />}
        {hasFilteredTools &&
          filteredTools.map((tool) => (
            <ToolServiceButton key={tool.id} tool={tool} mcpServer={serverMap.get(tool.serverId)} />
          ))}
      </Stack>
    </Stack>
  );
};

export default Tools;
